import type { CoachJourneyInput } from '../schema';
import { brandProfileSteps } from './brandDna/profileSteps';
import { firstBoardSteps } from './brandDna/firstBoardSteps';

/**
 * Brand DNA onboarding: the first thing a new trial member does. The Copilot leads the whole
 * thing as a conversation: it interviews them while their brand profile builds itself on the
 * board beside the chat, as a real board with two groups, My Business (what they say) and My
 * Voice and Content (how they say it). The profile is saved as their default, so every chat
 * writes as them. Then the same conversation moves to their first board (Expert Brain,
 * Inspiration Content, New Video Ideas) and ends on the aha: ten hooks and a script in their
 * own voice.
 *
 * No cards and no tour mode: the person only ever talks to Poppy. The journey does the
 * mechanics quietly (making the brand and its board, switching boards, laying out groups,
 * saving the default) and hears from the Copilot through its signals (`[[coach:…]]`, which the
 * app strips from what the person reads). Started from the flagged Tutorials library.
 */

export const brandDnaJourney: CoachJourneyInput = {
  id: 'brand-dna',
  signalPhrases: {
    record: ['and just talk 🎤', 'tap the mic and just talk'],
    voice: ['my business is done'],
    story: ['and tell me your story 🎤', 'tap the mic and tell me your story'],
    hooks: ['your board is built'],
    confirmed: ['next up: your first board'],
    // Poppy wrapping up before the read-back: the app asks for it (ask-readback).
    readback: ["that's everything", 'all set', 'ready to start creating', "whenever you're ready"],
  },
  version: 2,
  name: 'Brand DNA onboarding',
  description:
    'Chat with Poppy to build your brand profile and voice, then your first board, ending on 10 hooks and a script in your voice.',
  // The chat leads; the board stays as it is, no tour chrome and no camera jumps.
  mode: 'standard',
  dismissible: true,
  gate: { flag: 'scriptShootPost' },
  start: { link: true },
  tutorial: {
    description: 'Build your brand voice, then create your first hooks and script with Poppy.',
    on: 'new-board',
  },
  steps: [...brandProfileSteps, ...firstBoardSteps],
};
